import React, { useMemo, useState } from 'react';
import {
  Star,
  MapPin,
  Wifi,
  Car,
  Utensils,
  Dumbbell,
  Waves,
  Coffee,
  Wind,
  Shield,
  Users,
  Bed,
  Maximize,
} from 'lucide-react';
import { HOTEL_SEED, type HotelSeedItem } from '../data/hotelSeed';
import type { HotelDetail } from './HotelDetailsCard';
import styles from './HotelDetailsExpanded.module.scss';

const AMENITY_ICONS: Record<string, React.ComponentType<any>> = {
  'Free WiFi': Wifi,
  Pool: Waves,
  Gym: Dumbbell,
  Spa: Wind,
  Restaurant: Utensils,
  Bar: Coffee,
  Parking: Car,
  Breakfast: Coffee,
  'Room Service': Utensils,
  Concierge: Users,
  AC: Wind,
  Safe: Shield,
  'Hair Dryer': Wind,
  'Family Rooms': Users,
  'Sea View': Waves,
  Shuttle: Car,
  Valet: Car,
  'Bike Rental': Car,
  'Pet Friendly': Shield,
};

const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&h=700&fit=crop',
  'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1200&h=700&fit=crop',
  'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200&h=700&fit=crop',
  'https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=1200&h=700&fit=crop',
];

const toNumberPrice = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(String(value || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeHotel = (hotel: HotelDetail | HotelSeedItem): HotelDetail => {
  const nights = (hotel as HotelDetail).nights || (hotel as HotelSeedItem).nights || 4;
  const nightly =
    (hotel as HotelDetail).pricePerNight ||
    (hotel as HotelSeedItem).pricePerNight ||
    toNumberPrice((hotel as HotelSeedItem).price);
  const total =
    (hotel as HotelDetail).totalPrice ||
    (hotel as HotelSeedItem).totalPrice ||
    Math.round(nightly * nights);
  const rating =
    (hotel as HotelDetail).rating ||
    (hotel as HotelSeedItem).rating ||
    Math.min(9.9, 7.8 + ((hotel.stars || 0) * 0.3));
  const ratingLabel =
    (hotel as HotelDetail).ratingLabel ||
    (hotel as HotelSeedItem).ratingLabel ||
    (rating >= 9 ? 'Exceptional' : rating >= 8 ? 'Excellent' : 'Very Good');

  return {
    id: hotel.id,
    name: hotel.name,
    location: (hotel as HotelDetail).location || (hotel as HotelSeedItem).location || 'City Center',
    stars: hotel.stars,
    rating: Number(rating.toFixed(1)),
    ratingLabel,
    pricePerNight: nightly,
    totalPrice: total,
    currency: (hotel as HotelDetail).currency || (hotel as HotelSeedItem).currency,
    nights,
    image: hotel.image,
    images: (hotel as HotelDetail).images || (hotel as HotelSeedItem).images,
    rooms: (hotel as HotelDetail).rooms || (hotel as HotelSeedItem).rooms,
    roomsTotal: (hotel as HotelDetail).roomsTotal || (hotel as HotelSeedItem).roomsTotal,
    priceBands: (hotel as HotelDetail).priceBands || (hotel as HotelSeedItem).priceBands,
    amenities:
      (hotel as HotelDetail).amenities ||
      (hotel as HotelSeedItem).amenities ||
      ['Free WiFi', (hotel as HotelSeedItem).breakfastIncluded ? 'Breakfast' : 'Bar', 'Parking'],
    description:
      (hotel as HotelDetail).description ||
      (hotel as HotelSeedItem).description ||
      `${hotel.name} with ${hotel.stars}-star comfort and curated amenities.`,
    category:
      (hotel as HotelDetail).category ||
      (hotel as HotelSeedItem).category ||
      (hotel.stars >= 5 ? 'Luxury' : 'Premium'),
  };
};

export function HotelDetailsExpanded({
  hotel,
  onChooseRoom,
}: {
  hotel?: HotelDetail | HotelSeedItem;
  onChooseRoom?: (hotel: HotelDetail) => void;
}) {
  const normalized = useMemo(() => normalizeHotel(hotel || HOTEL_SEED[0]), [hotel]);
  const [activePhoto, setActivePhoto] = useState(0);

  const photos = normalized.images?.length
    ? normalized.images
    : [normalized.image, ...FALLBACK_IMAGES.filter((src) => src !== normalized.image)].slice(0, 4);
  const mapQuery = encodeURIComponent(`${normalized.name} ${normalized.location}`);

  return (
    <div className={styles.hdPage}>
      <div className={styles.hdCard}>
        <h1 className={styles.hdName}>{normalized.name}</h1>

        <div className={styles.hdMedia}>
          <div className={styles.hdPhotoMain}>
            <img src={photos[activePhoto]} alt={normalized.name} />
            <div className={styles.hdPhotoDots}>
              {photos.map((_, i) => (
                <button
                  key={`${normalized.id}-dot-${i}`}
                  type="button"
                  className={`${styles.hdDot} ${i === activePhoto ? styles.active : ''}`}
                  onClick={() => setActivePhoto(i)}
                  aria-label={`Show photo ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <div className={styles.hdMapContainer}>
            <iframe
              title="Hotel location"
              className={styles.hdMap}
              src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div className={styles.hdReviewBar}>
          <div className={styles.hdStars}>
            {Array.from({ length: 5 }, (_, i) => (
              <Star key={`star-${i}`} size={16} fill={i < normalized.stars ? 'currentColor' : 'none'} />
            ))}
          </div>
          <span className={styles.hdRatingBadge}>{normalized.rating.toFixed(1)}</span>
          <span className={styles.hdRatingLabel}>{normalized.ratingLabel}</span>
          <span className={styles.hdReviewCount}>120 reviews</span>
          <div className={styles.hdPriceSummary}>
            <span className={styles.hdPriceLabel}>from</span>
            <span className={styles.hdPriceAmount}>{`${normalized.currency || ''} ${normalized.totalPrice.toLocaleString()}`.trim()}</span>
            <span className={styles.hdPricePer}>{`${normalized.currency || ''} ${normalized.pricePerNight}`.trim()}/night</span>
          </div>
        </div>

        <div className={styles.hdAmenities}>
          <div className={styles.hdChip}>
            <MapPin size={14} />
            <span>{normalized.location}</span>
          </div>
          <div className={styles.hdChip}>
            <Bed size={14} />
            <span>Best for {normalized.nights} nights</span>
          </div>
          <div className={styles.hdChip}>
            <Maximize size={14} />
            <span>{normalized.category}</span>
          </div>
          {normalized.amenities.map((a) => {
            const Icon = AMENITY_ICONS[a] || Wifi;
            return (
              <div key={`${normalized.id}-${a}`} className={styles.hdChip}>
                <Icon size={14} />
                <span>{a}</span>
              </div>
            );
          })}
        </div>

        <p className={styles.hdDescription}>{normalized.description}</p>

        <button
          type="button"
          className={styles.hdChooseRoom}
          onClick={() => onChooseRoom?.(normalized)}
        >
          Choose a Room
        </button>
      </div>
    </div>
  );
}
